import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ActorLabel } from '~/components/common/actor-label';
import type { ActorRef } from '~/types/actor.types';
import { parseActorString } from '~/utils/actor';
import { cn } from '~/utils/cn';

interface ActorStringLabelProps {
  /** The workflow-service actor string; `null`/`undefined` renders `fallback`. */
  value: string | null | undefined;
  /** Show the leading avatar / icon tile. Turn off for dense table cells. */
  showAvatar?: boolean;
  /** Rendered when there is no actor at all. Defaults to `ActorLabel`'s em-dash. */
  fallback?: ReactNode;
  /** Show the actor's email under the name (detail panels, not table cells). */
  showEmail?: boolean;
  className?: string;
  /** `data-testid` root (default `actor-string-label`); the inner `ActorLabel` derives from it, plus `-on-behalf-of`. */
  testId?: string;
}

/**
 * Renders a workflow-service actor *string* through the shared `ActorLabel`, so a
 * string-based service and the integrations object envelope look identical. Adds
 * a muted "on behalf of X" note when an integration acted for a person.
 */
export function ActorStringLabel({
  value,
  showAvatar = true,
  fallback,
  showEmail = false,
  className,
  testId = 'actor-string-label',
}: ActorStringLabelProps) {
  const { t } = useTranslation('common');
  const parsed = parseActorString(value);

  if (!parsed) {
    return (
      <ActorLabel
        actor={null}
        testId={testId}
        showAvatar={showAvatar}
        fallback={fallback}
        {...(className ? { className } : {})}
      />
    );
  }

  // Machine-actor labels are the UI's to name; a person keeps their email
  // local-part (never title-cased — `vimal.ads` must not become "Vimal Ads").
  const displayName =
    parsed.kind === 'SYSTEM'
      ? t('actor.system')
      : parsed.kind === 'UNKNOWN'
        ? t('actor.unknown')
        : parsed.kind === 'USER' && parsed.email
          ? (parsed.email.split('@')[0] ?? parsed.raw)
          : parsed.raw;

  const actor: ActorRef = {
    id: null,
    displayName,
    kind: parsed.kind,
    email: parsed.email,
    avatarUrl: null,
    resolved: parsed.resolved,
  };

  const label = (
    <ActorLabel
      actor={actor}
      testId={testId}
      showAvatar={showAvatar}
      showEmail={showEmail}
      {...(className ? { className } : {})}
    />
  );

  if (!parsed.onBehalfOf) return label;

  return (
    <span className="inline-flex min-w-0 items-center gap-1" data-testid={`${testId}-wrapper`}>
      {label}
      <span
        className={cn('text-muted-foreground shrink-0 text-xs')}
        data-testid={`${testId}-on-behalf-of`}
      >
        ({t('actor.onBehalfOf', { name: parsed.onBehalfOf })})
      </span>
    </span>
  );
}
